// English — the source every other language is translated from.
// Placeholders in {braces} are filled in by the screen. "a | b" is singular | plural.
export default {
  nav: {
    home: "Home",
    search: "Search",
    explore: "Explore",
    messages: "Messages",
    tribzz: "Tribzz",
    channels: "Channels",
    settings: "Settings",
  },

  language: {
    title: "Language",
    lead: "Choose the language Tribzz uses for you. It follows your account to the app on your phone too.",
    note: "Posts and messages stay in the language people wrote them in. A few screens are still being translated and show in English for now.",
    saved: "✓ Saved",
    saving: "Saving…",
    error: "Couldn't save that. Check your connection and try again.",
    done: "Done",
  },

  settings: {
    kicker: "Your account",
    title: "Settings",
    editPhoto: "Edit profile photo",
    member: "Member",
    profileComplete: "Profile {pct}% complete",
    quickEdit: "👤 Edit profile",
    quickTribzz: "◆ My Tribzz",
    quickSecurity: "🔐 Security",
    finishTitle: "Finish your profile",
    finishSub: "Add your {fields} so people know it's you.",
    gathering: "Gathering your account…",
    fields: {
      name: "full name",
      image: "profile photo",
      email: "email",
      phone: "phone",
      description: "bio",
      more: "+{n} more",
    },
    sections: {
      account: "Account",
      membership: "Membership",
      create: "Create & earn",
      stuff: "Your stuff",
      community: "Community & apps",
      support: "Support",
    },
    rows: {
      personal: { title: "Personal information", sub: "Name, username, photo, interests, privacy" },
      profilePage: { title: "Your profile page", sub: "See what others see" },
      security: { title: "Security", sub: "Password and recent sign-ins" },
      notifications: { title: "Notifications", sub: "Phone alerts, email and quiet hours" },
      language: { title: "Language", sub: "The language Tribzz uses for you" },
      creator: { title: "Creator Dashboard", sub: "Earnings, true views, clips, drops and lives" },
      business: { title: "Business tools", sub: "Takeout, bookings, tickets and quotes for your Tribzz" },
      pro: { title: "Tribzz Pro", sub: "Offer your services — get verified, found and booked" },
      tickets: { title: "My tickets", sub: "Event tickets and their QR codes" },
      savedShorts: { title: "Saved shorts" },
      memories: { title: "Memories" },
      invite: { title: "Invite friends", sub: "Bring your people to Tribzz" },
      muzilly: { title: "Muzilly" },
      ideas: { title: "Ideas board", sub: "Vote on what Tribzz builds next" },
      help: { title: "Help & FAQ", sub: "Answers and support" },
      about: { title: "About Tribzz", sub: "Version, terms and privacy" },
    },
    pctLeft: "{n}% left",
    plan: {
      admin: "Admin",
      subscriber: "◆ Subscriber",
      free: "Free member",
    },
    membership: {
      fullAccess: "Full access",
      adminText: "Every feature, for testing. Not a paid plan, so there's nothing to manage or cancel.",
      adminUsage: "{n} live minutes used this month · no cap",
      perMonth: "$5 / month",
      usage: "{used} of {cap} live minutes used this month",
      manage: "Manage subscription",
      opening: "Opening…",
      kicker: "◆ Tribzz membership",
      bigLead: "Go live, post more,",
      bigAccent: "fund Tribzz.",
      perk1: "Post more than one photo at a time",
      perk2: "Go live, and watch other members' lives",
      perk3: "No ads, and your data is never sold",
      subscribe: "Subscribe · $5 / month",
      redirecting: "Redirecting…",
    },
    library: {
      shortsEmpty: "Shorts you saved & your playlists",
      saved: "{n} saved",
      playlists: "{n} playlist | {n} playlists",
      memoriesEmpty: "Your own stories, kept after 24 hours",
      memories: "{n} memory | {n} memories",
      freeSaves: "{used} of {limit} free saves used",
    },
    muzilly: {
      connectedAs: "Connected as {name}",
      yourAccount: "your Muzilly account",
      yourAccountTitle: "Your Muzilly account",
      connectPrompt: "Artists and listeners: connect your account",
      connected: "● Connected",
      linkedBody: "{name} — if you're an artist, what you post publicly here shows on your Muzilly page. If you're a listener, songs you hear here count toward the artists you follow.",
      intro: "Artists: your public posts show on your Muzilly page. Listeners: songs you hear here count toward the artists you follow.",
      step1: "Open Muzilly and go to your profile.",
      step2: "Tap Connect Tribzz to get an 8-character code.",
      step3: "Type it below — it works for 10 minutes.",
      codeLabel: "Muzilly code",
      close: "Close",
      cancel: "Cancel",
      disconnect: "Disconnect",
      working: "Working…",
      connect: "Connect",
      connecting: "Connecting…",
      errConnect: "Couldn't connect. Try a new code.",
      errDisconnect: "Couldn't disconnect.",
    },
    signOut: "Sign out",
    deleteAccount: "Delete account",
    deleteWarning: "Deleting your account is permanent and immediate. Your posts, messages, check-ins and connections are removed right away, and this can't be undone.",
    deleteButton: "Delete my account…",
    deleteTitle: "Delete your account?",
    deleteText: "This permanently and immediately removes your profile, posts, messages and connections. It can't be undone.",
    typeDelete: "Type DELETE to confirm",
    cancel: "Cancel",
    deleting: "Deleting…",
    deleteForever: "Delete forever",
  },

  home: {
    notifications: "Notifications",
    overline: "The People's Platform · {date}",
    welcome: "◆ Welcome back, {name} ◆",
    welcomeAnon: "◆ Welcome back ◆",
    deck: "Your circles, your culture — everything below is just today's front page.",
    news: {
      label: "Tribzz News",
      all: "All news →",
      listenTop: "Listen to the top story",
      listenLatest: "Listen to the latest · {n} stories",
      topStory: "★ TOP STORY",
      newsroom: "· Tribzz Newsroom",
      read: "Read the story →",
      minRead: "{n} min read",
      listen: "🎧 Listen",
    },
    topics: {
      local: "📍 Local",
      politics: "🗳 Politics",
      government: "🏛 Government",
      crime: "⚖️ Crime & courts",
      business: "💼 Business",
      education: "🎓 Education",
      health: "🩺 Health",
      sports: "🏀 Sports",
      culture: "🎨 Culture",
      tech: "💻 Tech",
      environment: "🌱 Environment",
      community: "🤝 Community",
      news: "📰 News",
    },
    ago: {
      minutes: "{n}m ago",
      hours: "{n}h ago",
      days: "{n}d ago",
    },
    share: "Share Something",
    watch: {
      label: "Watch & Create",
      browseChannels: "Browse Channels",
      startChannel: "Start a Channel",
      watchShorts: "Watch Shorts",
      browseStores: "Browse Stores",
      launchStore: "Launch a Store",
    },
    explore: {
      label: "From Explore",
      headLine1: "What's Being Said",
      headLine2: "Right Now",
      intro: "Explore is where the culture actually happens. A quick look at today's front page — the rest is one tap away.",
      loading: "Pulling today's edition…",
      house: "◆ Tribzz Daily",
      aMember: "A member",
      inGroup: "in {name}",
      readOn: "Read on Explore →",
      emptyTitle: "The front page is blank.",
      emptySub: "Nothing's been posted in the last 24 hours yet — be the first.",
      enter: "Enter Explore",
      vanishing: "Vanishing soon",
      hoursLeft: "{n}h left",
    },
    live: {
      label: "Happening Now",
      headA: "Public Tribzz",
      headLive: "Live",
      headB: "Right Now",
      intro: "Open to anyone — tap in and watch, even if you haven't joined yet.",
      badge: "● LIVE",
    },
    circles: {
      label: "Your Circles",
      headA: "Who's",
      headLive: "Live",
      headB: "Right Now",
      intro: "Every Tribzz is its own room, run by whoever started it. Here's who's actually live, and who's checked in, this minute.",
      loading: "Checking who's around…",
      hereNow: "{n} here now",
      nobody: "Nobody's checked in yet",
      emptyTitle: "You haven't joined a Tribzz yet.",
      emptySub: "A restaurant, a team, a person — anyone can start one. Find yours, or start it.",
      browse: "Browse Tribzz",
      start: "Start Your Own",
    },
    people: {
      label: "Your Circle",
      headA: "People to",
      headB: "Connect With",
      intro: "Shared Tribzz, shared city, shared interests — whatever you've got in common, we'll find it.",
      loading: "Finding your people…",
      activeNow: "Active now",
      member: "Member",
      inGroup: "In {name} with you",
      alsoInto: "Also into {interest}",
      view: "View Profile",
      connected: "Connected ✓",
      connect: "Connect",
      emptyTitle: "No suggestions right now.",
      emptySub: "Add your interests or location in Personal Information, or join a Tribzz — any of those helps us find your people.",
    },
    connections: {
      headA: "My",
      headB: "Connections",
      loading: "Loading your connections…",
      emptyTitle: "No connections yet.",
      emptySub: "Connect with people above and they'll show up here.",
    },
  },

  // Post categories: English uses the composer's own labels (constants/postCategories),
  // so only the other languages list them.

  // Tribzz Daily item types.
  daily: {
    question: "Question of the day",
    poll: "Poll",
    wyr: "Would you rather",
    quote: "Quote",
    trivia: "Trivia",
    fact: "Did you know?",
    riddle: "Riddle",
    history: "This day in history",
    recipe: "Quick recipe",
    diy: "How-to",
  },
};